import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';

import { KanbanServicosService } from '@/core/api/sicom-api/servicos/kanban/kanban-servicos.service';
import { type ArquivadasResumoResponse } from '@/core/api/sicom-api/servicos/kanban/kanban-servicos.model';
import { LoggerService } from '@/core/services/logger.service';
import { ArquivadosProtocolos } from '@/features/servicos/shared/arquivados-protocolos/arquivados-protocolos';
import { type TipoServicoPessoa } from '@/features/servicos/shared/enums/servico-disponivel.enum';
import { type KanbanColuna, KanbanProtocolos } from '@/features/servicos/shared/kanban-protocolos/kanban-protocolos';
import { type ItemStatus } from '@/features/servicos/shared/models/item-status.model';
import { TabContainer, type TabContainerOptionType } from '@/shared/components/layout/tab-container/tab-container';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
type FiltroPagamento = 'todos' | 'pagos' | 'nao-pagos' | 'sem-anuidade';

interface OpcaoFiltro {
    label: string;
    value: FiltroPagamento;
}

/**
 * Container do quadro de protocolos de um serviço: busca, filtro de pagamento e contadores,
 * entregando os itens filtrados ao kanban — a única visualização.
 */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-quadro-protocolos',
    standalone: true,
    imports: [DialogoPadraoDirective, FormsModule, DialogModule, KanbanProtocolos, ArquivadosProtocolos, TabContainer],
    templateUrl: './quadro-protocolos.html',
    styleUrl: './quadro-protocolos.scss'
})
export class QuadroProtocolos {
    private readonly kanbanServicosService = inject(KanbanServicosService);
    private readonly logger = inject(LoggerService);
    private readonly destroyRef = inject(DestroyRef);

    readonly filtroPagamento = signal<FiltroPagamento>('todos');
    readonly numeroProtocoloBusca = signal('');
    readonly exibirDialogFiltros = signal(false);
    readonly filtrosPagamento: OpcaoFiltro[] = [
        { label: 'Todos', value: 'todos' },
        { label: 'Pagos', value: 'pagos' },
        { label: 'Não pagos', value: 'nao-pagos' },
        { label: 'Sem anuidade', value: 'sem-anuidade' }
    ];

    readonly serviceName = input<string>();
    readonly itens = input<ItemStatus[]>([]);
    readonly carregando = input(false);
    readonly exibirFiltroAnuidade = input(false);
    /**
     * Repassado ao quadro: é o que decide se o card mostra o diretor técnico e de quem é o rosto no
     * avatar — do titular (pessoa física) ou do diretor técnico da empresa (pessoa jurídica).
     */
    readonly tipoPessoa = input<TipoServicoPessoa>('pessoa-fisica');
    readonly idServico = input<number | undefined>(undefined);
    readonly uf = input('');
    /** Repassado ao quadro: o gate de efetivar/cancelar no card é por código do serviço. */
    readonly codigoServico = input('');
    readonly itemSelected = output<ItemStatus>();
    /** Repassado do quadro: o movimento para a coluna de pendências pediu o aviso ao médico. */
    readonly avisoPendenciaSolicitado = output<ItemStatus>();
    /**
     * Repassado do quadro: os cards marcados numa coluna foram mandados para o encerramento em lote.
     * O container não decide nada sobre eles — quem confere e dispara é o diálogo, montado no
     * `dashboard-servicos`, que é o nível que sobrevive à destruição do quadro. `colunaDestino` faz
     * parte do payload pela mesma razão: só o quadro sabe qual coluna é a conclusiva.
     */
    readonly encerrarEmLoteSolicitado = output<{ itens: ItemStatus[]; coluna: KanbanColuna; colunaDestino: KanbanColuna }>();

    /**
     * O quadro em si. `undefined` na aba Arquivados, onde ele não está montado — e é por isso que
     * `recarregar()` tolera a ausência em vez de assumir que ele existe.
     */
    private readonly kanban = viewChild(KanbanProtocolos);

    /** Aba corrente. O quadro é o padrão: o arquivo é consulta ocasional. */
    readonly abaSelecionada = signal('QUADRO');

    /** Opções do alternador acima dos totalizadores: o quadro vivo e o arquivo de concluídos antigos. */
    readonly opcoesAba: TabContainerOptionType[] = [
        { label: 'Quadro', value: 'QUADRO' },
        { label: 'Arquivados', value: 'ARQUIVADOS' }
    ];

    /** Verdadeiro na aba Arquivados: muda o conteúdo da lista, os totalizadores e some o filtro de pagamento. */
    readonly noArquivo = computed(() => this.abaSelecionada() === 'ARQUIVADOS');

    /**
     * Totalizadores do arquivo, para os cards do topo trocarem de significado junto com a aba.
     * `null` significa "sem número confiável ainda" (antes de carregar, durante uma nova busca ou
     * após falha) — nunca é deixado com os valores do serviço ANTERIOR enquanto o novo carrega, e o
     * template mostra "—" nesse estado em vez de `0` (que passaria a significar três coisas: vazio,
     * carregando e falha).
     */
    readonly resumoArquivo = signal<ArquivadasResumoResponse | null>(null);

    /** Texto do card "Tempo médio" do arquivo: "—" enquanto `resumoArquivo` for `null`, sem o sufixo "dias" nesse estado. */
    readonly tempoMedioArquivoTexto = computed(() => {
        const resumo = this.resumoArquivo();
        return resumo === null ? '—' : `${resumo.tempoMedioDias} dias`;
    });

    /** Número digitado na busca, quando for um protocolo válido — o arquivo filtra no servidor por ele. */
    readonly numeroBuscado = computed(() => {
        const texto = this.numeroProtocoloBusca().trim();
        return texto && Number.isInteger(Number(texto)) ? Number(texto) : undefined;
    });

    constructor() {
        effect(() => {
            const emArquivo = this.noArquivo();
            // Lidos só para entrarem como dependência do efeito: servico/UF novos redisparam o
            // resumo enquanto a aba do arquivo estiver ativa.
            this.idServico();
            this.uf();
            if (emArquivo) {
                this.carregarResumoDoArquivo();
            }
        });
    }

    aoTrocarAba(opcao: { label: string; value: string }): void {
        this.abaSelecionada.set(opcao.value);
    }

    get titulo(): string {
        return this.serviceName() || 'Serviço Selecionado';
    }

    get filtrosPagamentoVisiveis(): OpcaoFiltro[] {
        return this.exibirFiltroAnuidade() ? this.filtrosPagamento : this.filtrosPagamento.filter((filtro) => filtro.value !== 'sem-anuidade');
    }

    get filtroPagamentoAplicado(): FiltroPagamento {
        return this.exibirFiltroAnuidade() || this.filtroPagamento() !== 'sem-anuidade' ? this.filtroPagamento() : 'todos';
    }

    get localizacaoAtiva(): boolean {
        return this.numeroProtocoloNormalizado.length > 0;
    }

    get totalProtocolos(): number {
        return this.itens().length;
    }

    get totalPagos(): number {
        return this.itens().filter((item) => item.inPago === true && item.isAnuidadePaga !== false).length;
    }

    get totalPendencias(): number {
        return this.itens().filter((item) => item.inPago === false || item.isAnuidadePaga === false).length;
    }

    get possuiFiltroAtivo(): boolean {
        return this.filtroPagamentoAplicado !== 'todos' || this.localizacaoAtiva;
    }

    /**
     * Relê o quadro. Repasse, e não uma segunda busca: quem sabe carregar colunas, solicitações
     * rastreadas e concluídos é o `KanbanProtocolos`, e duplicar isso aqui criaria duas verdades sobre
     * o que o quadro mostra. Existe porque quem pede a recarga (o diálogo do encerramento em lote) vive
     * no `dashboard-servicos`, um nível ACIMA — e de lá o quadro é o único caminho até o kanban.
     */
    recarregar(): void {
        this.kanban()?.recarregar();
    }

    onSelect(item: ItemStatus): void {
        this.itemSelected.emit(item);
    }

    selecionarFiltroPagamento(filtro: FiltroPagamento): void {
        this.filtroPagamento.set(filtro);
        this.exibirDialogFiltros.set(false);
    }

    limparLocalizacao(): void {
        this.numeroProtocoloBusca.set('');
    }

    limparFiltros(): void {
        this.filtroPagamento.set('todos');
        this.numeroProtocoloBusca.set('');
    }

    get itensFiltrados(): ItemStatus[] {
        const itensPorPagamento = (() => {
            switch (this.filtroPagamentoAplicado) {
                case 'pagos':
                    return this.itens().filter((item) => item.inPago === true && item.isAnuidadePaga !== false);
                case 'nao-pagos':
                    return this.itens().filter((item) => item.inPago === false);
                case 'sem-anuidade':
                    return this.itens().filter((item) => item.isAnuidadePaga === false);
                default:
                    return this.itens();
            }
        })();

        if (!this.localizacaoAtiva) {
            return itensPorPagamento;
        }

        const protocoloBusca = this.numeroProtocoloNormalizado;

        return itensPorPagamento.filter((item) => this.numeroProtocoloFiltravel(item).includes(protocoloBusca));
    }

    private get numeroProtocoloNormalizado(): string {
        return this.normalizarProtocolo(this.numeroProtocoloBusca());
    }

    private numeroProtocoloFiltravel(item: ItemStatus): string {
        return this.normalizarProtocolo(item.numeroProtocolo || item.portal || item.sas);
    }

    private normalizarProtocolo(valor?: string | number | null): string {
        return String(valor ?? '').replace(/\D/g, '');
    }

    /**
     * Busca os totalizadores do arquivo. Falhar aqui não pode custar a lista: o resumo é o topo da
     * tela, não o conteúdo dela — por isso o erro só é registrado, nunca propagado.
     */
    private carregarResumoDoArquivo(): void {
        const idServico = this.idServico();
        const uf = this.uf();
        if (idServico == null || !uf) {
            return;
        }

        // "Última pedida é a que fica" — mesmo guard de `ArquivadosProtocolos.carregarPagina` (irmão
        // desta tela), copiado de propósito para os dois ficarem reconhecíveis: sem ele, trocar de
        // serviço/UF enquanto a aba do arquivo está aberta pode disparar uma segunda chamada, e se a
        // resposta do serviço ANTERIOR chegar depois da do serviço ATUAL, ela sobrescreve os totais
        // certos com os de outro serviço — sem erro visível, só números errados na tela.
        const aindaCorrente = (): boolean => this.idServico() === idServico && this.uf() === uf;

        // Zera ANTES de disparar a busca nova: enquanto a resposta do serviço/UF atual não chega, a
        // tela não pode continuar mostrando o total do serviço ANTERIOR — ver doc-comment de `resumoArquivo`.
        this.resumoArquivo.set(null);

        this.kanbanServicosService
            .resumoArquivadas(idServico, uf)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (resumo) => {
                    if (!aindaCorrente()) {
                        return;
                    }
                    this.resumoArquivo.set(resumo);
                },
                error: (falha: unknown) => {
                    if (!aindaCorrente()) {
                        return;
                    }
                    this.resumoArquivo.set(null);
                    this.logger.error('Falha ao carregar o resumo do arquivo:', falha);
                }
            });
    }
}
